import React, { useState } from "react";
import { Box, Typography, IconButton, Popover } from "@mui/material";
import PaletteOutlinedIcon from "@mui/icons-material/PaletteOutlined";

import MessageList from "./components/MessageList";

import { screenWrapperSx } from "./MessagePreviewScreen.styles";

import Background from "../../../../assets/ilustrations/Background.png";

const getEmailPreviewContent = (messages = []) => {
  const firstFilledMessage =
    messages.find((message) => {
      const value = message?.content ?? message?.html ?? "";
      return String(value).trim().length > 0;
    }) || messages?.[0] || {};
  const rawContent = firstFilledMessage?.content || firstFilledMessage?.html || "";
  const normalizedContent = String(rawContent).trim();
  const hasHtmlMarkup = /<\/?[a-z][\s\S]*>/i.test(normalizedContent);
  const emailImageUrl = firstFilledMessage?.header?.type === "img"
    ? firstFilledMessage?.header?.content
    : "";
  const isImageUploading = Boolean(firstFilledMessage?.header?.uploading);
  const savedEmailSubtitle = String(firstFilledMessage?.email_subtitle || "").trim();
  const savedEmailAccentColor = String(firstFilledMessage?.email_accent_color || "").trim();

  return {
    messageId: firstFilledMessage?.id || "auto-reply",
    content: rawContent,
    isHtml: hasHtmlMarkup,
    imageUrl: emailImageUrl,
    isImageUploading,
    savedEmailSubtitle,
    savedEmailAccentColor,
  };
};

const MessagePreviewScreen = ({
  channel,
  messages = [],
  contentInsetTop = 0,
  emailSubtitle = "",
  emailAccentColor = "",
  emailAccentColors = [],
  onChangeEmailAccentColor,
  onChangeMessageButtons,
  disabledButtons,
  hideMediaConfigMenu = false,
  highlightIncomingMessage = false,
  onPickImage,
  onPickPdf,
  onReplaceMedia,
  onRemoveMedia,
  allowedButtonTypes,
}) => {
  const previewChannel = channel;
  const isTemplateMobileLayout = ["whatsapp_api", "whatsapp_web", "sms"].includes(previewChannel);
  const isSms = previewChannel === "sms";
  const [colorAnchorEl, setColorAnchorEl] = useState(null);

  if (previewChannel === "email") {
    const {
      messageId,
      content,
      isHtml,
      imageUrl,
      isImageUploading,
      savedEmailSubtitle,
      savedEmailAccentColor,
    } = getEmailPreviewContent(messages);
    const canPickEmailImage = Boolean(onPickImage) && !disabledButtons;
    const canPickAccentColor = Boolean(onChangeEmailAccentColor) && !disabledButtons;
    const headerText = emailSubtitle || savedEmailSubtitle || "";
    const resolvedAccentColor = emailAccentColor || savedEmailAccentColor || "#782dc8";
    const introText = content || "";
    const isColorPopoverOpen = Boolean(colorAnchorEl);
    const normalizedAccent = String(resolvedAccentColor || "").toUpperCase();
    const isLightAccent = normalizedAccent === "#FFFFFF" || normalizedAccent === "WHITE";
    const headerTextColor = isLightAccent ? "#111827" : "#FFFFFF";
    const logoPlaceholderBorder = isLightAccent
      ? "2px dashed rgba(17,24,39,0.35)"
      : "2px dashed rgba(255,255,255,0.65)";
    const logoPlaceholderHover = isLightAccent
      ? "rgba(17,24,39,0.06)"
      : "rgba(255,255,255,0.08)";

    return (
      <Box
        sx={{
          ...screenWrapperSx,
          display: "flex",
          flexDirection: "column",
          textAlign: "left",
          alignItems: "stretch",
          justifyContent: "flex-start",
          minHeight: 0,
          p: 1,
          pt: contentInsetTop ? `${contentInsetTop + 8}px` : 1,
        }}
      >
        <Box
          sx={{
            width: "100%",
            height: "100%",
            flex: 1,
            minHeight: 0,
            border: "1px solid #e0e0e0",
            borderRadius: 1,
            overflowY: isHtml ? "hidden" : "auto",
            overflowX: "hidden",
            backgroundColor: "#fff",
          }}
        >
          {isHtml ? (
            <iframe
              title="Email HTML Preview"
              srcDoc={content}
              sandbox="allow-popups allow-popups-to-escape-sandbox"
              style={{
                width: "100%",
                height: "100%",
                border: "none",
                background: "#fff",
                display: "block",
              }}
            />
          ) : (
            <Box
              sx={{
                backgroundColor: "#fff",
                height: "100%",
                minHeight: "100%",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Box sx={{ backgroundColor: resolvedAccentColor, p: 3, textAlign: "center", position: "relative" }}>
                {canPickAccentColor && (
                  <>
                    <IconButton
                      size="small"
                      onClick={(event) => setColorAnchorEl(event.currentTarget)}
                      sx={{
                        position: "absolute",
                        top: 12,
                        right: 12,
                        backgroundColor: "rgba(255,255,255,0.9)",
                        "&:hover": { backgroundColor: "#fff" },
                      }}
                    >
                      <PaletteOutlinedIcon
                        sx={{ color: isLightAccent ? "#111827" : resolvedAccentColor, fontSize: 19 }}
                      />
                    </IconButton>
                    <Popover
                      open={isColorPopoverOpen}
                      anchorEl={colorAnchorEl}
                      onClose={() => setColorAnchorEl(null)}
                      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                      transformOrigin={{ vertical: "top", horizontal: "right" }}
                    >
                      <Box sx={{ p: 1.2, display: "flex", alignItems: "center", gap: 0.8 }}>
                        {emailAccentColors.map((color) => {
                          const isActive = color === resolvedAccentColor;
                          const isWhite = String(color).toUpperCase() === "#FFFFFF";
                          return (
                            <Box
                              key={color}
                              component="button"
                              type="button"
                              onClick={() => {
                                onChangeEmailAccentColor(color);
                                setColorAnchorEl(null);
                              }}
                              aria-label={`Selecionar cor ${color}`}
                              sx={{
                                width: 30,
                                height: 30,
                                borderRadius: 0.9,
                                border: isActive
                                  ? "2px solid #111827"
                                  : isWhite
                                    ? "1px solid #D1D5DB"
                                    : "1px solid #ffffff",
                                outline: isActive ? "2px solid #E5E7EB" : "none",
                                backgroundColor: color,
                                cursor: "pointer",
                              }}
                            />
                          );
                        })}
                      </Box>
                    </Popover>
                  </>
                )}
                {imageUrl ? (
                  <Box
                    component="img"
                    src={imageUrl}
                    alt="Logo da empresa"
                    onClick={() => canPickEmailImage && onPickImage(messageId)}
                    sx={{
                      maxWidth: 220,
                      width: "100%",
                      height: "auto",
                      cursor: canPickEmailImage ? "pointer" : "default",
                      borderRadius: 1,
                      border: canPickEmailImage ? logoPlaceholderBorder : "none",
                      p: canPickEmailImage ? 0.7 : 0,
                      transition: "opacity 0.2s ease",
                      "&:hover": canPickEmailImage
                        ? { opacity: 0.9 }
                        : {},
                    }}
                  />
                ) : (
                  <Box
                    onClick={() => canPickEmailImage && onPickImage(messageId)}
                    sx={{
                      width: 140,
                      height: 140,
                      mx: "auto",
                      borderRadius: 1,
                      border: logoPlaceholderBorder,
                      color: headerTextColor,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 13,
                      fontWeight: 600,
                      px: 1.5,
                      cursor: canPickEmailImage ? "pointer" : "default",
                      transition: "background-color 0.2s ease",
                      "&:hover": canPickEmailImage
                        ? { backgroundColor: logoPlaceholderHover }
                        : {},
                    }}
                  >
                    Sua logo aqui
                  </Box>
                )}
                {canPickEmailImage && Boolean(imageUrl) && (
                  <Typography sx={{ color: headerTextColor, fontSize: 12, mt: 1 }}>
                    Clique na imagem para alterar
                  </Typography>
                )}
                {isImageUploading && (
                  <Typography sx={{ color: headerTextColor, fontSize: 12, mt: 0.5 }}>
                    Enviando imagem...
                  </Typography>
                )}
                {!!headerText && (
                  <Typography
                    sx={{
                      color: headerTextColor,
                      fontSize: 30,
                      fontWeight: 700,
                      mt: 2,
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      overflowWrap: "anywhere",
                      maxWidth: "100%",
                    }}
                  >
                    {headerText}
                  </Typography>
                )}
              </Box>

              <Box sx={{ backgroundColor: "#fff", p: 3, flex: 1 }}>
                {Boolean(introText.trim()) && (
                  <Typography
                    sx={{
                      fontSize: 14,
                      lineHeight: 1.6,
                      color: "#353334",
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      overflowWrap: "anywhere",
                      maxWidth: "100%",
                      mb: 2,
                    }}
                  >
                    {introText}
                  </Typography>
                )}
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    );
  }

  if (isTemplateMobileLayout) {
    return (
      <Box
        sx={{
          width: "100%",
          height: "100%",
          borderRadius: "10px",
          ...(isSms
            ? {
                backgroundColor: "#eef2f7",
              }
            : {
                backgroundColor: "#d4cec5",
                backgroundImage: `url(${Background}), url(${Background}), url(${Background}), linear-gradient(#cec8bf, #cec8bf)`,
                backgroundRepeat: "repeat, repeat, repeat, no-repeat",
                backgroundPosition: "top left, top left, top left, center",
                backgroundSize: "280px auto, 280px auto, 280px auto, cover",
                backgroundBlendMode: "multiply, multiply, multiply, normal",
              }),
          p: 1.25,
          pt: contentInsetTop ? `${contentInsetTop + 10}px` : 1.25,
          border: isSms ? "1px solid #dde3ec" : "1px solid #cec8bf",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <MessageList
          messages={messages}
          onChangeMessageButtons={onChangeMessageButtons}
          channel={previewChannel}
          disabledButtons={disabledButtons}
          hideMediaConfigMenu={hideMediaConfigMenu}
          highlightIncomingMessage={highlightIncomingMessage}
          onPickImage={onPickImage}
          onPickPdf={onPickPdf}
          onReplaceMedia={onReplaceMedia}
          onRemoveMedia={onRemoveMedia}
          allowedButtonTypes={allowedButtonTypes}
        />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        borderRadius: "6px",
        backgroundImage: `url(${Background})`,
        backgroundColor: "#fff",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        display: "flex",
        flexDirection: "column",
        pt: contentInsetTop ? `${contentInsetTop}px` : 0,
      }}
    >
      <MessageList
        messages={messages}
        onChangeMessageButtons={onChangeMessageButtons}
        channel={previewChannel}
        disabledButtons={disabledButtons}
        hideMediaConfigMenu={hideMediaConfigMenu}
        highlightIncomingMessage={highlightIncomingMessage}
        onPickImage={onPickImage}
        onPickPdf={onPickPdf}
        onReplaceMedia={onReplaceMedia}
        onRemoveMedia={onRemoveMedia}
        allowedButtonTypes={allowedButtonTypes}
      />
    </Box>
  );
};

export default MessagePreviewScreen;
